import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, ChevronRight, Home, Lock } from 'lucide-react';
import { dashboardApi } from '../../services/dashboard-api.service';
import type {
  TerritorialPending as TerritorialPendingType,
  TerritorialPendingChild,
  TerritorialPendingCounts,
  TerritorialPendingTasks,
  TerritorialLevel,
  PendingTaskGroup,
} from '../../types/dashboard.types';

const GRANT_OPTIONS = ['All', 'CRR', 'MRR', 'SDF', 'FDR', 'PMGSY', 'MGNREGS', 'DMFT', 'IMS'];

// Human label for the level whose children are currently listed.
const LEVEL_LABEL: Record<TerritorialLevel, string> = {
  territory: 'Territories',
  circle: 'Circles',
  division: 'Divisions',
  subdivision: 'Sub-divisions',
  mandal: 'Mandals',
  work: 'Works',
};

// The 9 pending-task tiles, in display order. Drives both the scope summary card
// and the per-child breakdown table so the two never diverge.
const TILES: { key: keyof TerritorialPendingCounts; label: string; cls: string }[] = [
  { key: 'ts_to_be_accorded', label: 'TS to be accorded', cls: 'text-blue-700' },
  { key: 'tender_stage', label: 'Tender stage', cls: 'text-indigo-700' },
  { key: 'tender_under_process', label: 'Tender u/process', cls: 'text-indigo-700' },
  { key: 'agreements_pending', label: 'Agreements', cls: 'text-purple-700' },
  { key: 'cr_approvals_pending', label: 'CR approvals', cls: 'text-orange-700' },
  { key: 'final_payment_pending', label: 'FPP pending', cls: 'text-amber-700' },
  { key: 'pending_updation', label: 'Pending updation', cls: 'text-rose-700' },
  { key: 'eoat_works', label: 'EOAT', cls: 'text-red-700' },
  { key: 'forest_clearance', label: 'Forest clearance', cls: 'text-green-700' },
];

// Per-tile work lists shown at the mandal (work) leaf level.
const LEAF_TILES: { key: keyof TerritorialPendingTasks; label: string }[] = [
  { key: 'ts_to_be_accorded', label: 'TS to be accorded' },
  { key: 'tender_stage', label: 'Tender stage' },
  { key: 'agreements_pending', label: 'Agreements pending' },
  { key: 'cr_approvals_pending', label: 'CR approvals pending' },
  { key: 'final_payment_pending', label: 'FPP pending' },
  { key: 'pending_updation', label: 'Pending updation' },
  { key: 'eoat_works', label: 'EOAT works' },
  { key: 'forest_clearance', label: 'Forest clearance' },
];

// One hop in the drill-down trail. `childLevel` is what gets listed when this
// node is the active scope; `parentId` is the node being drilled into.
interface PathNode {
  name: string;
  childLevel: TerritorialLevel;
  parentId?: number;
}

const ROOT: PathNode = { name: 'All Territories', childLevel: 'territory', parentId: undefined };

// ==========================================
// SCOPE SUMMARY CARD (pending tiles for the active scope)
// ==========================================

const ScopeSummary: React.FC<{ counts?: TerritorialPendingCounts; asOnDate: string }> = ({ counts, asOnDate }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="bg-blue-700 text-white px-6 py-3">
      <h2 className="text-base font-bold">PENDING TASKS TO BE MONITORED STATUS AS ON {asOnDate}</h2>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-gray-200">
      {TILES.map(t => (
        <div key={t.key} className="bg-white px-4 py-3 text-center">
          <div className={`text-2xl font-bold ${t.cls}`}>{counts?.[t.key] ?? 0}</div>
          <div className="text-xs text-gray-500 mt-1">{t.label}</div>
        </div>
      ))}
      <div className="bg-gray-50 px-4 py-3 text-center">
        <div className="text-2xl font-bold text-gray-800">{counts?.total_pending ?? 0}</div>
        <div className="text-xs text-gray-500 mt-1">Total pending</div>
      </div>
    </div>
  </div>
);

// ==========================================
// CHILD BREAKDOWN TABLE (one row per child node, click to drill)
// ==========================================

const ChildBreakdownTable: React.FC<{
  childLevel: TerritorialLevel;
  children: TerritorialPendingChild[];
  onDrill: (c: TerritorialPendingChild) => void;
}> = ({ childLevel, children, onDrill }) => {
  const sum = (key: keyof TerritorialPendingCounts) => children.reduce((s, c) => s + (c.pending[key] || 0), 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-700 text-white px-6 py-2.5">
        <h3 className="text-sm font-bold">{LEVEL_LABEL[childLevel]} &mdash; click a row to drill down</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-3 py-2 border text-left font-semibold w-12">Sno</th>
              <th className="px-3 py-2 border text-left font-semibold">{LEVEL_LABEL[childLevel].replace(/s$/, '')}</th>
              {TILES.map(t => (
                <th key={t.key} className="px-2 py-2 border text-center font-semibold text-gray-700">{t.label}</th>
              ))}
              <th className="px-2 py-2 border text-center font-semibold">Total</th>
              <th className="px-2 py-2 border w-8"></th>
            </tr>
          </thead>
          <tbody>
            {children.map((c, i) => (
              <tr key={`${c.level}-${c.id}`} className="text-center hover:bg-blue-50 cursor-pointer" onClick={() => onDrill(c)}>
                <td className="px-3 py-2 border">{i + 1}</td>
                <td className="px-3 py-2 border text-left font-medium text-blue-700">{c.name}</td>
                {TILES.map(t => (
                  <td key={t.key} className={`px-2 py-2 border ${c.pending[t.key] ? t.cls : 'text-gray-300'}`}>{c.pending[t.key]}</td>
                ))}
                <td className="px-2 py-2 border font-semibold">{c.pending.total_pending}</td>
                <td className="px-2 py-2 border text-gray-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
            {children.length === 0 && (
              <tr><td colSpan={TILES.length + 4} className="px-4 py-8 text-center text-gray-400">No {LEVEL_LABEL[childLevel].toLowerCase()} found</td></tr>
            )}
            {children.length > 0 && (
              <tr className="text-center font-bold bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-2 border" colSpan={2}>Total</td>
                {TILES.map(t => (
                  <td key={t.key} className="px-2 py-2 border">{sum(t.key)}</td>
                ))}
                <td className="px-2 py-2 border">{sum('total_pending')}</td>
                <td className="px-2 py-2 border"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==========================================
// WORKS LEAF — per-tile work lists for the mandal
// ==========================================

const TileWorksTable: React.FC<{ title: string; groups: PendingTaskGroup[] }> = ({ title, groups }) => {
  const works = groups.flatMap(g => g.works);
  if (works.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-700 text-white px-6 py-2.5">
        <h3 className="text-sm font-bold">{title} ({works.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              {['Sno', 'Work ID', 'Name of the work', 'Grant', 'Type', 'Estimate Cost', 'Sanctioned Length', 'Stage of work'].map(h => (
                <th key={h} className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap border-b border-gray-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {works.map((w, idx) => (
              <tr key={`${w.work_id}-${idx}`} className="hover:bg-blue-50">
                <td className="px-2 py-1.5">{idx + 1}</td>
                <td className="px-2 py-1.5">{w.work_id}</td>
                <td className="px-2 py-1.5"><span className="max-w-[260px] block truncate" title={w.work_name}>{w.work_name}</span></td>
                <td className="px-2 py-1.5">{w.grant_type}</td>
                <td className="px-2 py-1.5">{w.work_type || '-'}</td>
                <td className="px-2 py-1.5">{w.estimate_cost.toFixed(2)}</td>
                <td className="px-2 py-1.5">{w.length_sanctioned_km}</td>
                <td className="px-2 py-1.5">{w.sub_stage_of_work || w.abstract_stage_of_work || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WorksLeaf: React.FC<{ tasks: TerritorialPendingTasks | null }> = ({ tasks }) => {
  if (!tasks) return null;
  const anyWorks = LEAF_TILES.some(t => (tasks[t.key] || []).some(g => g.works.length > 0));
  return (
    <div className="space-y-4">
      {LEAF_TILES.map(t => (
        <TileWorksTable key={t.key} title={t.label} groups={tasks[t.key] || []} />
      ))}
      {!anyWorks && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-8 text-center text-gray-400">
          No pending tasks in this mandal
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const TerritorialPending: React.FC = () => {
  const [path, setPath] = useState<PathNode[]>([ROOT]);
  const [grant, setGrant] = useState('All');
  const [data, setData] = useState<TerritorialPendingType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [scopeName, setScopeName] = useState('');

  // Lock the drill-down to the user's jurisdiction: start the breadcrumb AT their node when their
  // level has an engineering child node; a DISTRICT scope keeps the root (no node in this hierarchy)
  // but is still badged and backend scope-enforced.
  useEffect(() => {
    dashboardApi.getMyScope().then((s: any) => {
      if (!s || s.isState || !s.scopeId) return;
      const childOf: Record<string, string> = { TERRITORY: 'circle', CIRCLE: 'division', DIVISION: 'subdivision', SUB_DIVISION: 'mandal', MANDAL: 'work' };
      setLocked(true);
      setScopeName(`${s.name || s.level} (${String(s.level).replace('_', ' ').toLowerCase()})`);
      if (childOf[s.level]) setPath([{ name: s.name || s.level, childLevel: childOf[s.level] as any, parentId: s.scopeId }]);
    }).catch(() => {});
  }, []);

  const current = path[path.length - 1];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const g = grant === 'All' ? undefined : grant;
      const result = await dashboardApi.getTerritorialPending(current.childLevel, current.parentId, g);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load drill-down');
    } finally {
      setLoading(false);
    }
  }, [current.childLevel, current.parentId, grant]);

  useEffect(() => { loadData(); }, [loadData]);

  const drillInto = (c: TerritorialPendingChild) => {
    setPath(prev => [...prev, { name: c.name, childLevel: c.next_level, parentId: c.id }]);
  };

  const goToCrumb = (idx: number) => {
    setPath(prev => prev.slice(0, idx + 1));
  };

  const asOnDate = data?.as_on_date || new Date().toISOString().split('T')[0];

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-800">Pending Tasks to be Monitored &mdash; Territorial Drill-down</h1>
          <p className="text-xs text-gray-500">Territory &rarr; Circle &rarr; Division &rarr; Sub-division &rarr; Mandal &rarr; Works</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Grant</label>
          <select
            value={grant}
            onChange={e => setGrant(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
          >
            {GRANT_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button onClick={loadData} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1 mb-4 text-sm">
        {locked && (
          <span className="inline-flex items-center gap-1 mr-1 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5"
            title="Data is limited to your assigned jurisdiction">
            <Lock className="h-3 w-3" /> {scopeName || 'your scope'}
          </span>
        )}
        {path.map((p, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <ChevronRight className="h-4 w-4 text-gray-400" />}
            <button
              onClick={() => goToCrumb(idx)}
              className={`flex items-center gap-1 px-2 py-1 rounded ${idx === path.length - 1 ? 'font-semibold text-blue-700 bg-blue-50' : 'text-blue-600 hover:underline'}`}
            >
              {idx === 0 && <Home className="h-3.5 w-3.5" />}
              {p.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={loadData} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Loading drill-down data...</span>
        </div>
      ) : data && (
        <div className="space-y-6">
          <ScopeSummary counts={data.scope_pending} asOnDate={asOnDate} />
          {current.childLevel === 'work'
            ? <WorksLeaf tasks={data.pending_tasks} />
            : <ChildBreakdownTable childLevel={data.level || current.childLevel} children={data.children} onDrill={drillInto} />}
        </div>
      )}
    </div>
  );
};

export default TerritorialPending;
